import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/v2/Logo";
import { useSiteContent } from "@/lib/useSiteContent";

const REASSURANCE = ["Étude gratuite", "Sans engagement", "Particuliers & professionnels", "Accompagnement national"];

// Mini-graphique de marché animé, façon dashboard "Évolution des prix".
function HeroSpark() {
  const N = 44;
  const PLOT = { x0: 26, x1: 232, y0: 10, y1: 78 }; // zone de tracé
  const X = (i) => PLOT.x0 + (i * (PLOT.x1 - PLOT.x0)) / (N - 1);
  // Séries pseudo-aléatoires déterministes et volatiles (façon prix spot).
  const mk = (seed, drift) =>
    Array.from({ length: N }, (_, i) => {
      const n =
        Math.sin(i * 0.9 + seed) * 11 +
        Math.sin(i * 2.3 + seed * 2) * 6 +
        Math.sin(i * 4.7 + seed) * 3.2 +
        Math.sin(i * 8.1 + seed * 3) * 1.6;
      const y = 44 + drift * (i - N / 2) * 0.55 + n;
      return Math.max(PLOT.y0 + 2, Math.min(PLOT.y1 - 2, y));
    });
  const elecY = mk(0.6, 0.85); // électricité : légère détente
  const gazY = mk(2.4, -0.75); // gaz : légère hausse
  const toPath = (ys) => ys.map((y, i) => `${i === 0 ? "M" : "L"}${X(i).toFixed(1)},${y.toFixed(1)}`).join(" ");
  const elec = toPath(elecY);
  const gaz = toPath(gazY);
  const elecArea = `${elec} L${PLOT.x1},${PLOT.y1} L${PLOT.x0},${PLOT.y1} Z`;
  const yTicks = [
    { y: 12, v: "120" },
    { y: 33, v: "90" },
    { y: 55, v: "60" },
    { y: 76, v: "30" },
  ];
  const xLabels = ["Mai", "Août", "Nov", "Fév", "Mai"];
  return (
    <div className="rounded-2xl border border-white/15 bg-white/[0.06] p-4 backdrop-blur-md">
      <div className="mb-1.5">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/70">Évolution des prix · €/MWh</span>
      </div>
      <svg viewBox="0 0 244 96" className="w-full" role="img" aria-label="Évolution des prix de l'électricité et du gaz">
        <defs>
          <linearGradient id="em-spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5FD068" stopOpacity="0.26" />
            <stop offset="100%" stopColor="#5FD068" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* grille + axe Y */}
        {yTicks.map((t) => (
          <g key={t.y}>
            <line x1={PLOT.x0} y1={t.y} x2={PLOT.x1} y2={t.y} stroke="rgba(255,255,255,0.09)" strokeWidth="1" />
            <text x={PLOT.x0 - 5} y={t.y + 3} textAnchor="end" fontSize="7" fill="rgba(255,255,255,0.45)">{t.v}</text>
          </g>
        ))}
        {/* axe X */}
        {xLabels.map((m, i) => (
          <text key={m + i} x={PLOT.x0 + (i * (PLOT.x1 - PLOT.x0)) / (xLabels.length - 1)} y="92" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.45)">{m}</text>
        ))}
        <path d={elecArea} fill="url(#em-spark-fill)" />
        {/* courbes toujours visibles */}
        <path d={gaz} fill="none" stroke="#F0554A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d={elec} fill="none" stroke="#5FD068" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        {/* reflet lumineux qui défile en continu */}
        <path d={gaz} fill="none" stroke="#FF9488" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="em-spark-comet em-spark-comet--2" />
        <path d={elec} fill="none" stroke="#C4F042" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className="em-spark-comet" />
        <circle cx={X(N - 1)} cy={gazY[N - 1]} r="3" fill="#F0554A" className="em-spark-dot em-spark-dot--2" />
        <circle cx={X(N - 1)} cy={elecY[N - 1]} r="3" fill="#5FD068" className="em-spark-dot" />
      </svg>
      <div className="mt-1.5 flex items-center gap-4 text-[11px] text-white/70">
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-3 rounded" style={{ background: "#5FD068" }} /> Électricité</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-3 rounded" style={{ background: "#F0554A" }} /> Gaz</span>
      </div>
    </div>
  );
}

// Hero de page : photo plein cadre + dégradé vert, texte à gauche, carte points
// clés à droite, et bandeau de réassurance intégré en bas du hero.
export default function PageHeroPhoto({
  eyebrow,
  title,
  subtitle,
  image,
  cta = true,
  ctaLabel = "Demander une étude gratuite",
  ctaTo = "/contact?type=etude",
  highlights,
  highlightIcons = [],
  highlightImages = [],
  highlightsLabel = "Notre accompagnement",
  highlightsNote = "",
  highlightsNumbered = false,
  bottomCards = null,
  reassurance = null,
  imagePosition = "center",
  highlightsBelow = false,
  parallax = false,
  overlay = "brand",
  flipLogo = false,
  heroChart = false,
  heroLogo = false,
  heroAside = null,
  wideCard = false,
  contentBottom = false,
  cmsKey = "",
}) {
  // Clés CMS du verso de la carte (partagées entre toutes les pages hero)
  const VERSO_KEYS = [
    "v2.herocard.logo", "v2.herocard.line1", "v2.herocard.line2_prefix",
    "v2.herocard.line2_accent", "v2.herocard.note", "v2.herocard.bg_color", "v2.herocard.bg_image",
  ];
  // Surcharge éditable via le CMS (clés v2.<cmsKey>.hero.* + verso), avec repli sur les props.
  const cc = useSiteContent([
    ...(cmsKey
      ? [`v2.${cmsKey}.hero.eyebrow`, `v2.${cmsKey}.hero.title`, `v2.${cmsKey}.hero.subtitle`, `v2.${cmsKey}.hero.image`]
      : []),
    ...VERSO_KEYS,
  ]);
  if (cmsKey) {
    eyebrow = cc(`v2.${cmsKey}.hero.eyebrow`, eyebrow);
    title = cc(`v2.${cmsKey}.hero.title`, title);
    subtitle = cc(`v2.${cmsKey}.hero.subtitle`, subtitle);
    image = cc(`v2.${cmsKey}.hero.image`, image);
  }
  // webp : dérivé seulement pour les fichiers locaux png/jpg ; sinon on garde l'image telle quelle.
  const webp = image && /\.(png|jpe?g)$/i.test(image) ? image.replace(/\.(png|jpe?g)$/i, ".webp") : image;
  const sectionRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (parallax !== true) return; // mode "fixed" géré en CSS, pas de JS
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const sec = sectionRef.current;
      const img = imgRef.current;
      if (!sec || !img) return;
      const rect = sec.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      img.style.transform = `translate3d(0, ${rect.top * 0.12}px, 0)`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [parallax]);

  return (
    <section ref={sectionRef} className="relative isolate -mt-20 flex min-h-[88svh] lg:min-h-[100svh] flex-col overflow-hidden border-b border-border">
      <div className="absolute inset-0 -z-10 bg-primary">
        {parallax === "fixed" ? (
          <div
            className="absolute inset-0 bg-cover md:bg-fixed"
            style={{
              backgroundImage: `url(${webp || image})`,
              backgroundPosition: imagePosition === "center" ? "center" : imagePosition,
            }}
          />
        ) : (
          <picture>
            {webp && <source srcSet={webp} type="image/webp" />}
            <img
              ref={imgRef}
              src={image}
              alt=""
              loading="eager"
              fetchpriority="high"
              decoding="async"
              style={{ objectPosition: imagePosition }}
              className={`absolute w-full object-cover ${parallax === true ? "inset-x-0 -top-[7%] h-[114%] will-change-transform" : "inset-0 h-full"}`}
            />
          </picture>
        )}
        {overlay === "cinematic-soft" ? (
          <>
            {/* Voile minimal : juste de quoi tenir le contraste du texte à gauche. */}
            {/* Teinte verte de marque : mix-blend-color ne touche que la couleur,
               pas la luminosité — la photo ne s'assombrit pas. */}
            <div className="absolute inset-0 bg-primary opacity-30 mix-blend-color" />
            {/* La photo reste claire à droite ; on ne rattrape le contraste que
               là où il y a du texte : colonne de gauche et pied de hero. */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/70 from-0% via-primary/22 via-[38%] to-transparent to-[62%]" />
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(52% 46% at 22% 46%, rgba(6,45,32,0.58) 0%, rgba(6,45,32,0.28) 52%, transparent 76%)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-primary/25 md:bg-transparent" />
          </>
        ) : overlay === "cinematic-light" ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
            <div className="absolute inset-0" style={{ background: "radial-gradient(135% 115% at 72% 30%, transparent 0%, rgba(0,0,0,0.30) 95%)" }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-black/30 md:bg-transparent" />
          </>
        ) : overlay === "brand-light" ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-primary/70 via-primary/35 to-transparent" />
            <div className="absolute inset-0" style={{ background: "radial-gradient(135% 110% at 70% 30%, transparent 0%, rgba(0,0,0,0.3) 95%)" }} />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/55 via-primary/10 to-transparent" />
            <div className="absolute inset-0 bg-primary/25 md:bg-transparent" />
          </>
        ) : overlay === "cinematic" ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-transparent" />
            <div className="absolute inset-0" style={{ background: "radial-gradient(135% 115% at 72% 30%, transparent 0%, rgba(0,0,0,0.55) 92%)" }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
            <div className="absolute inset-0 bg-black/40 md:bg-transparent" />
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/70 to-transparent" />
            <div className="absolute inset-0" style={{ background: "radial-gradient(135% 110% at 70% 30%, transparent 0%, rgba(0,0,0,0.5) 95%)" }} />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/15 to-transparent" />
            <div className="absolute inset-0 bg-primary/35 md:bg-transparent" />
          </>
        )}
      </div>

      {/* Contenu principal */}
      <div className={`em-container flex w-full flex-1 pt-28 ${contentBottom ? "items-end pb-5 lg:pb-6" : highlightsBelow ? "items-end pb-16 lg:pb-24" : "items-center pb-8"}`}>
        <div className={`grid w-full items-center gap-8 ${highlightsBelow ? "" : "lg:grid-cols-12 lg:gap-12"}`}>
          <div className={highlightsBelow ? "" : heroChart || wideCard ? "lg:col-span-5" : "lg:col-span-6"}>
            {eyebrow && <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{eyebrow}</p>}
            <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight text-white">{title}</h1>
            {subtitle && <p className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-white/85">{subtitle}</p>}
            {cta && (
              <div className="mt-7">
                <Button asChild className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
                  {ctaTo.startsWith("#") ? (
                    <a href={ctaTo}>
                      {ctaLabel} <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  ) : (
                    <Link to={ctaTo}>
                      {ctaLabel} <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  )}
                </Button>
              </div>
            )}
          </div>

          {highlights?.length > 0 && (() => {
            const list = (
              <>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent">{highlightsLabel}</p>
                {highlightsNumbered ? (
                  <ol className="relative mt-5 lg:mt-7">
                    {highlights.map((h, i) => (
                      <li key={h} className="relative flex items-center gap-4 pb-7 last:pb-0">
                        {/* trait de liaison entre les pastilles */}
                        {i < highlights.length - 1 && (
                          <span aria-hidden className="absolute left-[19px] top-10 h-[calc(100%-1.5rem)] w-px bg-white/25" />
                        )}
                        <span className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent font-display text-base font-bold text-accent-foreground">
                          {i + 1}
                        </span>
                        <span className="text-base lg:text-lg leading-relaxed text-white/95">{h}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                <ul className={`mt-5 lg:mt-7 ${highlightImages.length || highlightIcons.length ? "space-y-5 lg:space-y-6" : "space-y-4 lg:space-y-5"}`}>
                  {highlights.map((h, i) => {
                    const img = highlightImages[i];
                    const Ic = highlightIcons[i] || Check;
                    const big = highlightImages.length > 0 || highlightIcons.length > 0;
                    return (
                      <li key={h} className="flex items-center gap-4">
                        {img ? (
                          <img src={img} alt="" aria-hidden className="h-12 w-12 shrink-0" />
                        ) : (
                          <span className={`flex shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground ${big ? "h-12 w-12" : "mt-0.5 h-7 w-7"}`}>
                            <Ic className={big ? "h-6 w-6" : "h-4 w-4"} strokeWidth={big ? 2 : 3} aria-hidden />
                          </span>
                        )}
                        <span className={`text-base lg:text-lg leading-relaxed text-white/95 ${heroAside ? "lg:whitespace-nowrap" : ""}`}>{h}</span>
                      </li>
                    );
                  })}
                </ul>
                )}
              </>
            );
            const rightPanel = heroChart ? (
              <HeroSpark />
            ) : heroAside ? (
              heroAside
            ) : heroLogo ? (
              <div className="flex flex-col items-center text-center">
                <Logo dark className="h-24 md:h-28 drop-shadow-[0_8px_24px_rgba(0,0,0,0.4)]" />
                <span aria-hidden className="mt-5 h-[3px] w-12 rounded-full bg-accent" />
                <p className="mt-4 font-display text-base md:text-lg font-semibold text-white">
                  Votre énergie, <span className="text-accent">mieux négociée.</span>
                </p>
              </div>
            ) : null;
            const content = rightPanel ? (
              <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-8">
                <div className={heroAside ? "lg:flex-[1.7]" : "lg:flex-1"}>{list}</div>
                <div className="lg:flex-1">{rightPanel}</div>
              </div>
            ) : (
              list
            );
            const widthCls = highlightsBelow ? "max-w-md lg:max-w-2xl" : "max-w-md lg:max-w-3xl";
            return (
              <div className={highlightsBelow ? "" : heroChart || wideCard ? "lg:col-span-7" : "lg:col-span-6"}>
                {flipLogo ? (
                  <div className={`em-flip em-flip--hover relative w-full cursor-pointer ${widthCls}`}>
                    {heroAside && (
                      /* Calque flouté placé hors de la structure 3D du flip (le preserve-3d
                         bloque le backdrop-filter des descendants dans Chrome). Flou fort à
                         gauche, dégressif vers la droite : la zone du « ? » reste transparente.
                         Il s'estompe au survol pour laisser le verso intact. */
                      <div aria-hidden className="em-hero-veil pointer-events-none absolute inset-0 overflow-hidden rounded-3xl">
                        {/* mobile : panneau flouté uniforme */}
                        <span className="absolute inset-0 bg-white/[0.06] backdrop-blur-xl lg:hidden" />
                        {/* desktop : flou progressif — couches empilées partant du bord gauche,
                            de plus en plus courtes ; les flous se cumulent sans jonction visible */}
                        <div className="absolute inset-0 hidden lg:block">
                          {[
                            ["80%", "1.5px"],
                            ["74%", "2px"],
                            ["68%", "3px"],
                            ["62%", "4.5px"],
                            ["58%", "6px"],
                            ["54%", "8px"],
                          ].map(([w, b]) => (
                            <span
                              key={w}
                              className="absolute inset-y-0 left-0"
                              style={{ width: w, backdropFilter: `blur(${b})`, WebkitBackdropFilter: `blur(${b})` }}
                            />
                          ))}
                        </div>
                        {/* teinte en dégradé continu (au-dessus des paliers, gomme les jonctions) */}
                        <span
                          className="absolute inset-0 hidden lg:block"
                          style={{ background: "linear-gradient(to right, rgba(255,255,255,0.05) 56%, rgba(255,255,255,0) 84%)" }}
                        />
                      </div>
                    )}
                    <div className="em-flip__inner">
                      <div className={`em-flip__face rounded-3xl border border-white/15 p-6 shadow-2xl lg:px-10 lg:py-7 ${heroAside ? "" : "bg-white/[0.06] backdrop-blur-md"}`}>
                        {content}
                      </div>
                      <div
                        className="em-flip__face em-flip__back em-run-border relative flex flex-col items-center justify-center overflow-hidden rounded-3xl px-8 py-6 shadow-2xl ring-1 ring-white/15 backdrop-blur-[5px]"
                        style={{ background: cc("v2.herocard.bg_color", "") || "linear-gradient(135deg, rgba(16,82,58,0) 0%, rgba(10,59,42,0) 45%, rgba(5,36,26,0.04) 100%)" }}
                      >
                        {/* image de fond optionnelle (CMS) */}
                        {cc("v2.herocard.bg_image", "") && (
                          <img aria-hidden src={cc("v2.herocard.bg_image", "")} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40" />
                        )}
                        {/* halo lumineux lime derrière le logo */}
                        <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(58% 52% at 50% 40%, rgba(196,240,66,0.22), transparent 70%)" }} />
                        {/* reflet en haut — effet verre */}
                        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-white/12 to-transparent" />
                        {/* reflet diagonal subtil */}
                        <span aria-hidden className="pointer-events-none absolute -inset-x-12 -top-1/3 h-[90%] rotate-[18deg] bg-gradient-to-b from-white/8 to-transparent blur-2xl" />
                        {/* liseré intérieur */}
                        <span aria-hidden className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/10" />
                        <div className="relative flex flex-col items-center text-center">
                          <img src={cc("v2.herocard.logo", "/img/logo-energy-market-blanc.png")} alt="Energy Market" className="h-28 md:h-36 w-auto drop-shadow-[0_10px_30px_rgba(0,0,0,0.45)]" />
                          <span aria-hidden className="mt-6 h-[3px] w-14 rounded-full bg-accent" />
                          <div className="mt-5 w-[21rem] max-w-full font-display text-xl md:text-2xl font-semibold text-white">
                            <span className="block text-2xl md:text-[1.7rem] text-justify [text-align-last:justify]"><span className="em-mark text-primary">{cc("v2.herocard.line1", "L'énergie au meilleur prix.")}</span></span>
                            <span className="block text-[1.4rem] md:text-2xl text-justify [text-align-last:justify]">{cc("v2.herocard.line2_prefix", "Vos contrats,")} <span className="text-accent">{cc("v2.herocard.line2_accent", "mieux négociés.")}</span><sup className="text-accent">*</sup></span>
                          </div>
                          <p className="mt-4 max-w-xs text-[10px] leading-snug text-white/60">
                            {cc("v2.herocard.note", "* Recherche de l'offre la plus adaptée parmi les fournisseurs et offres accessibles au moment de l'analyse, selon votre profil de consommation.")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`em-run-border relative w-full rounded-3xl border border-white/12 bg-white/[0.03] p-5 shadow-2xl backdrop-blur-[6px] lg:p-6 ${widthCls}`}>
                    {content}
                  </div>
                )}
                {/* Mention légale : sous la carte, alignée sur son bord droit */}
                {highlightsNote && (
                  <p className={`mt-3 w-full text-right text-[11px] lg:text-xs leading-snug text-white/60 ${widthCls}`}>
                    {highlightsNote}
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Vignettes (types de travaux) — bande basse du hero, au-dessus de la réassurance */}
      {bottomCards?.length > 0 && (
        <div className="relative">
          <div className="em-container pb-8 lg:pb-10">
            <ul className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-5">
              {bottomCards.map((c) => (
                <li key={c.label} className="h-full">
                  <Link
                    to={c.to}
                    className="group relative block h-full overflow-hidden rounded-2xl border border-white/15 shadow-xl transition duration-300 hover:-translate-y-1 hover:border-accent/70"
                  >
                    <span className="relative block aspect-[292/160] w-full overflow-hidden">
                      {c.image ? (
                        <img
                          src={c.image}
                          alt=""
                          aria-hidden
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <span aria-hidden className="absolute inset-0 bg-white/10" />
                      )}
                    </span>
                    <span className="flex items-center justify-between gap-3 bg-[rgba(6,32,23,0.72)] px-3 py-2.5 backdrop-blur-md transition-colors duration-300 group-hover:bg-[rgba(6,32,23,0.85)] lg:px-4 lg:py-3">
                      <span className="min-w-0 text-[14px] font-semibold leading-tight text-white lg:text-base">
                        {c.label}
                      </span>
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground transition-transform duration-300 group-hover:translate-x-1 lg:h-9 lg:w-9">
                        <ArrowRight className="h-4 w-4" strokeWidth={2.5} aria-hidden />
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Bandeau de réassurance — intégré au bas du hero */}
      <div className="relative border-t border-white/15">
        <div className="em-container grid grid-cols-2 gap-x-4 gap-y-3 py-5 md:grid-cols-4">
          {(reassurance || REASSURANCE.map((r) => ({ label: r }))).map(({ label, icon: Ic = Check }) => (
            <div key={label} className="group flex items-center gap-2 rounded-full px-2 py-1 -mx-2 text-sm font-medium text-white cursor-default transition-all duration-300 hover:bg-accent hover:text-accent-foreground">
              <Ic className="h-4 w-4 shrink-0 text-accent transition-colors duration-300 group-hover:text-accent-foreground" aria-hidden /> {label}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
